import { RouteLocationNormalized, RouteMeta } from 'vue-router'

import {
  AppNavigationGuard,
  AppRouteMeta,
  createMfWrapper,
  MicroFrontendLogger,
} from '@sennder/shell-utilities'
import {
  ISennTmsSharedData,
  opsProfileMock,
} from '@sennder/senn-node-microfrontend-interfaces'

import router, { INITIAL_ROUTE_PATH } from '@/router'
import {
  getStateCallbacks,
  getStateData,
  getStateProviders,
  SennTmsStoreData,
} from '@/store'
import { logger } from '@/services/logger/loggers'
import {
  AppAnalyticsProvider,
  wrapWithDeprecationWarning,
} from '@/services/analyticsProvider'
import { isLocalEnv } from '@/common/config'
import { loggerInstance } from '@/services/logger'
import { microfrontends } from '@/config/microfrontends'
import { AppRoute, routes } from '@/config/routes'
import { loadUserData } from '@/store/loadUserData'
import { auth } from '@/modules/auth'
import { i18n } from '@/services/i18n'

const isStoreReady = (
  data: ISennTmsSharedData | SennTmsStoreData
): data is ISennTmsSharedData => {
  if ('user' in data && data.profile !== opsProfileMock) {
    return true
  }

  return false
}

export const getMicrofrontendData = (npmName: string) => {
  const data = getStateData()

  // It's critical that getMicrofrontendData returns a reactive value for watchers down the line to work
  if (isStoreReady(data)) {
    return data
  }

  // Auth-MF accepts incomplete state, typecast required
  if (npmName === 'internal-auth-mf-component-v2') {
    return data as ISennTmsSharedData
  }

  throw new Error(`state.data.profile is not initialized (Loading ${npmName})`)
}

export const getMfContext = async (
  npmName: string,
  analyticsProvider: AppAnalyticsProvider
) => {
  return {
    data: getMicrofrontendData(npmName),
    callbacks: getStateCallbacks(),
    providers: {
      ...getStateProviders(),
      // segment is deprecated, but we still want to provide it to micro-frontends that use it until we remove it
      segment: wrapWithDeprecationWarning(analyticsProvider),
    },
  }
}

/**
 * Micro-frontends with routes
 */
type AppRouteMicrofrontendId = (typeof routes)[AppRoute]['mf']

const addAppRoute = (
  routePath: AppRoute,
  routeMeta: AppRouteMeta<AppRouteMicrofrontendId>
) => {
  const module = microfrontends[routeMeta.mf]

  const component = async () => {
    const analyticsProvider = new AppAnalyticsProvider(
      routeMeta.context.analytics
    )
    const mfLogger = new MicroFrontendLogger(
      routeMeta.context.logger,
      () => loggerInstance
    )

    return createMfWrapper<ISennTmsSharedData>({
      allowEnvironmentOverrides: isLocalEnv(),
      getData: async () => {
        const ctx = await getMfContext(module.npmName, analyticsProvider)

        /**
         * TODO: Revisit this override.
         * Historically, syncParentRouter we passed to page-level micro-frontends was always an empty functions, with no clear reason.
         * Now, the actual implementation of syncParentRouter is defined in the store and passed down to micro-frontends through the context.
         * This override ensures that page-level micro-frontends still receive an empty function for syncParentRouter, while other micro-frontends receive the actual implementation.
         */
        return {
          ...ctx,
          callbacks: {
            ...ctx.callbacks,
            syncParentRouter: () => {},
          },
        }
      },
      hooks: {
        failure: logger.error,
      },
      mf: {
        id: routeMeta.name,
        fml: module,
        context: {
          analytics: routeMeta.context.analytics,
          logger: routeMeta.context.logger,
        },
      },
      providers: {
        getAnalytics: () => analyticsProvider,
        getLogger: () => mfLogger,
      },
      router,
    })
  }

  const guards = routeMeta.guards || []

  router.addRoute({
    path: routePath,
    name: routeMeta.name,
    component,
    meta: {
      layout: routeMeta.fullscreenLayout ? 'AppLayoutAuth' : 'AppLayoutDefault',
      public: routeMeta.public,
      guards,
      npmName: module.npmName,
      title: `navigation.item.${routeMeta.name}`,
    },
    children: [
      {
        path: ':catchAll(.*)',
        meta: {
          guards,
          npmName: module.npmName,
        },
        component,
      },
    ],
  })
}

export const registerRoutes = () => {
  initRouteGuards()
  Object.entries(routes).forEach(([path, route]) =>
    addAppRoute(path as AppRoute, route)
  )

  router.addRoute({
    path: '/:catchAll(.*)',
    name: 'not-found',
    component: () => import('../NotFound.vue'),
    meta: {
      title: 'navigation.item.not-found',
    },
  })

  router.addRoute({
    path: '/',
    redirect: { path: INITIAL_ROUTE_PATH },
  })
}

export const initRouteGuards = () => {
  router.beforeEach(async (to, from) => {
    await checkUserDataLoaded(from, to)
    return routeNavigationGuard(to, from)
  })

  router.beforeResolve((to) => {
    const { name, meta } = to
    setPageTitle(String(name), meta)
  })
}

const setPageTitle = (name: string, meta: RouteMeta): void => {
  const pageName =
    (meta.title && i18n.global.t(meta.title)) ||
    name.charAt(0).toUpperCase() + name.slice(1)
  document.title = `${pageName} |  sennTMS - sennder`
}

const LOGIN_ROUTE_NAME = routes['/login'].name

async function checkUserDataLoaded(
  from: RouteLocationNormalized,
  to: RouteLocationNormalized
) {
  // If we are navigating from login page to another page, we need to load user data
  if (from.name === LOGIN_ROUTE_NAME && to.name !== LOGIN_ROUTE_NAME) {
    await loadUserData(true)
  }
}

const isAuthenticatedGuard: AppNavigationGuard = async (
  to: RouteLocationNormalized
) => {
  if (await auth.value?.isAuthenticated()) {
    return true
  } else {
    return {
      name: LOGIN_ROUTE_NAME,
      query: { redirectTo: encodeURIComponent(to.fullPath) },
    }
  }
}

const isNotAuthenticatedGuard: AppNavigationGuard = async () => {
  if (await auth.value?.isAuthenticated()) {
    return { path: INITIAL_ROUTE_PATH }
  } else {
    return true
  }
}

export const routeNavigationGuard: AppNavigationGuard = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized
) => {
  const guards = [
    to.meta.public ? isNotAuthenticatedGuard : isAuthenticatedGuard,
    ...(to.meta.guards || []),
  ]
  if (guards.length === 0) {
    return true
  }

  for (const guard of guards) {
    const result = await guard(to, from)
    if (
      typeof result === 'object' ||
      typeof result === 'string' ||
      result === false
    ) {
      return result
    }
  }

  return true
}
